import { Card, Center, Flex, Heading, Td, Tr } from "@chakra-ui/react";
import React, { useCallback, useState, useEffect } from "react";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import store from "../../redux/store";
import { get_full_product_list } from "../../redux/productSlice";
import TableView from "../../components/TableView";
import { omcOptions } from "../../constant/data";
import CustomSingleInput from "../../components/CustomSingleInput";
import CustomButton from "../../components/CustomButton";
import { baseUrl, formatIndianPrice } from "../../utils/utils";
import { get_vehicle_list } from "../../redux/vehiclSlice";
import { get_vendor_list } from "../../redux/vendorSlice";
import { get_purchase_list } from "../../redux/purchaseSlice";

// Updated validation schema
const AddProductSchema = Yup.object().shape({
  omc: Yup.string().required("OMC selection is required"),
  INV_no: Yup.string()
    .required("INV Number is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "INV Number must contain only letters and numbers"
    ),
  vendor_id: Yup.string().required("Vendor ID is required"),
  INV_date: Yup.string().required("INV date is required"),
});

// Single product validation schema
const SingleProductSchema = Yup.object().shape({
  product: Yup.string().required("Product selection is required"),
  emptyReturn: Yup.string().required("Empty return is required"),
  unit_rate: Yup.number()
    .required("Unit rate is required")
    .min(0.01, "Unit rate must be greater than 0"),
  discount: Yup.number()
    .min(0, "Discount cannot be negative")
    .test(
      "discount-validation",
      "Discount cannot be greater than unit rate",
      function (value) {
        const { unit_rate } = this.parent;
        return !value || !unit_rate || value <= unit_rate;
      }
    ),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0.01, "Quantity must be greater than 0"),
  vehicle_no: Yup.string().required("Vehicle number is required"),
});

const AddPurchase = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { full_product_list } = useSelector((state) => state.products);
  const { vendor_list } = useSelector((state) => state.vendors);

  const [inventory_list, setInventory_list] = useState([]);
  const [loading, setLoading] = useState(false);

  const [single_product, setSingle_product] = useState({});
  const [products_array, setProducts_array] = useState([]);

  const [singleProductErrors, setSingleProductErrors] = useState({});
  const [isValidatingSingleProduct, setIsValidatingSingleProduct] =
    useState(false);

  const vendorList = vendor_list?.map((val, ind) => ({
    value: val?.id,
    label: val.legalname,
  }));
  const product_list = full_product_list?.map((val, ind) => ({
    value: val?.product_code,
    label: val.description + "-" + val?.category + "-" + val?.type,
    ...val,
  }));

  const filtered_product_list = (type) =>
    product_list?.filter((v) => v.type == type);

  const initialValues = {
    omc: "",
    INV_amount: "",
    INV_date: "",
    vendor_id: "",
    INV_no: "",
  };

  useEffect(() => {
    store.dispatch(get_full_product_list());
    store.dispatch(get_vendor_list({ page: 1, rows: 10000 }));
  }, []);

  // Enhanced handleChangeProduct with validation
  const handleChangeProduct = async (key, value) => {
    const updatedProduct = { ...single_product, [key]: value };
    setSingle_product(updatedProduct);
    validateSingleProduct(updatedProduct); // validate the updated object directly
    const validateTimeout = setTimeout(() => {
      validateSingleProduct(updatedProduct);
    }, 10);
    if (validateTimeout) clearTimeout(validateTimeout);
  };

  // const handleChangeProduct = async (key, value) => {
  //   const updatedProduct = { ...single_product, [key]: value };
  //   setSingle_product(updatedProduct);
  //   await validateSingleProduct(single_product);
  //   // Validate on change (debounced)
  //   const validateTimeout = setTimeout(() => {
  //     validateSingleProduct(updatedProduct);
  //   }, 10);
  //   if (validateTimeout) clearTimeout(validateTimeout);
  // };
  // Validation function for single product
  const validateSingleProduct = async (productData) => {
    try {
      await SingleProductSchema.validate(productData, { abortEarly: false });
      setSingleProductErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setSingleProductErrors(errors);
      return false;
    }
  };
  const handleRemoveInventory = (id) => {
    setProducts_array((prevList) => prevList.filter((item, ind) => ind !== id));
  };

  const fetchGST = (omc, key, uuid) => {
    const product = filtered_product_list(omc);
    const gst_value = product?.filter((v) => v.value == uuid);

    setSingle_product((pre) => ({ ...pre, [key]: gst_value[0]?.gst }));
  };

  // Enhanced onAdd with validation
  const onAdd = async (values, { resetForm, setSubmitting }) => {
    try {
      setSubmitting(true);
      setIsValidatingSingleProduct(true);

      // Validate single product before adding
      const isValid = await validateSingleProduct(single_product);

      if (!isValid) {
        toast.error("Please fix the errors in the product form");
        return;
      }

      // Check for duplicate products (optional)
      const isDuplicate = products_array.some(
        (product) => product.product === single_product.product
      );

      if (isDuplicate) {
        toast.error("This product is already added");
        return;
      }

      // Calculate amounts before adding
      const calculatedProduct = {
        ...single_product,
        taxable_amount:
          (single_product.unit_rate - (single_product.discount || 0)) *
          single_product.quantity,
        gst_amount:
          ((single_product.unit_rate - (single_product.discount || 0)) *
            single_product.quantity *
            single_product.gst) /
          100,
      };

      setProducts_array((prevList) => [...prevList, calculatedProduct]);
      setSingle_product({});
      setSingleProductErrors({});

      // toast.success("Product added successfully");
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("Error adding product");
    } finally {
      setSubmitting(false);
      setIsValidatingSingleProduct(false);
    }
  };

  // Enhanced calculate_amount function
  const calculate_amount = () => {
    if (
      !single_product?.unit_rate ||
      !single_product?.discount ||
      !single_product?.quantity ||
      !single_product.gst
    ) {
      toast.error("Please enter gst, unit rate, discount and quantity first");
      return;
    }

    const unit_rate = parseInt(single_product.unit_rate) || 0;
    const discount = parseInt(single_product.discount) || 0;
    const quantity = parseInt(single_product.quantity) || 0;
    const gst = parseInt(single_product.gst) || 0;

    if (discount > unit_rate) {
      toast.error("Discount cannot be greater than unit rate");
      return;
    }

    const taxable_amount = (unit_rate - discount) * quantity;
    const gst_amount = (taxable_amount * gst) / 100;
    console.log(taxable_amount, gst_amount);

    const updatedProduct = {
      ...single_product,
      taxable_amount: taxable_amount,
      gst_amount: gst_amount,
    };
    setSingle_product(updatedProduct);
    // setSingle_product((pre) => ({
    //   ...pre,

    //   taxable_amount: taxable_amount,
    //   gst_amount: gst_amount,
    // }));
  };

  const calculateTotals = () => {
    const totalTaxable = products_array?.reduce(
      (sum, item) => sum + (Number(item.taxable_amount) || 0),
      0
    );

    const totalGST = products_array?.reduce(
      (sum, item) => sum + (Number(item.gst_amount) || 0),
      0
    );

    return (totalTaxable + totalGST).toFixed(2);
  };

  console.log("singleProductErrors", singleProductErrors);
  const handleSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);

        const formData = new FormData();
        console.log("grandTotal", calculateTotals());

        formData.append("action", "create");
        formData.append("type", values?.omc);
        formData.append("inv_no", values?.INV_no);
        formData.append("inv_date", values?.INV_date);
        formData.append("vendor_id", values?.vendor_id);
        formData.append("total_amount", calculateTotals());

        products_array?.forEach((v) => {
          formData.append("uuid[]", v.product);
          formData.append("hsn[]", v.p_detail?.hsn_no);
          formData.append("gst[]", v.gst);
          formData.append("unit[]", v.p_detail?.uom);
          formData.append("unit_price[]", v.unit_rate);
          formData.append("discount[]", v.discount);
          formData.append("gst_amount[]", v.gst_amount);
          formData.append("category[]", v.p_detail?.category);
          formData.append("empty_returned[]", v.emptyReturn);
          formData.append("qty[]", v.quantity);
          formData.append("vehicle_no[]", v.vehicle_no);
        });
        // Add edit mode ID if available

        const response = await fetch(baseUrl + "purchase.php", {
          method: "POST",
          body: formData,
        });
        const result = await response.json();

        // Fix: Better response checking
        if (response.ok && (result.success || result.status === "success")) {
          toast.success(result?.message);
          store.dispatch(get_purchase_list({ page: 1, rows: 5 }));

          navigate(-1);
        } else {
          toast.error(result?.message);
          throw new Error(
            result.message || result.error || "An error occurred"
          );
        }
      } catch (error) {
        console.error("Inventory submission error:", error);

        if (
          error.message.toLowerCase().includes("duplicate") ||
          error.message.toLowerCase().includes("already exists")
        ) {
          toast.error("Some inventory items already exist");
        } else if (error.message.toLowerCase().includes("unauthorized")) {
          toast.error("Session expired. Please login again.");
          navigate("/login");
        } else {
          const errorMessage = "Failed to add inventory. Please try again.";

          toast.error(error.message || errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate, inventory_list, setInventory_list, id, calculateTotals] // Added id dependency
  );
  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
      >
        <Heading fontSize={"xl"}>{"Add Purchase"}</Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      {inventory_list?.length >= 4 || (
        <Formik
          key={"add"}
          initialValues={initialValues}
          validationSchema={AddProductSchema}
          onSubmit={onAdd}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            isValid,
            dirty,
            submitForm,
          }) => (
            <Form>
              <Card padding={5}>
                {/* Row 1: Product Selection + Units */}

                <CustomInput
                  label1={"OMC Plant"}
                  value1={values.omc}
                  onChange1={(v) => {
                    setFieldValue("omc", v.target.value);
                    setSingle_product({});
                  }}
                  onBlur1={handleBlur("omc")}
                  select1={true}
                  options1={omcOptions}
                  placeholder1={"Select OMC"}
                  error1={touched.omc && errors.omc}
                  required1={true}
                  label2={"INV No."}
                  value2={values.INV_no}
                  onChange2={handleChange("INV_no")}
                  onBlur2={handleBlur("INV_no")}
                  placeholder2={"Enter INV Number"}
                  error2={touched.INV_no && errors.INV_no}
                  required2={true}
                />

                {/* Row 2: Opening Stock + Current Stock */}
                <CustomInput
                  select1
                  label1={"Vendor ID"}
                  value1={values.vendor_id}
                  onChange1={handleChange("vendor_id")}
                  options1={vendorList}
                  onBlur1={handleBlur("vendor_id")}
                  placeholder1={"Enter Vendor ID"}
                  error1={touched.vendor_id && errors.vendor_id}
                  required1={true}
                  label2={"INV Date"}
                  value2={values.INV_date}
                  onChange2={handleChange("INV_date")}
                  onBlur2={handleBlur("INV_date")}
                  placeholder2={"Enter current stock"}
                  error2={touched.INV_date && errors.INV_date}
                  required2={true}
                  type2={"date"}
                />
                <CustomInput
                  disabled1
                  label1={"INV Amount"}
                  value1={calculateTotals() || 0}
                  placeholder1={"INV Amount"}
                />

                <TableView
                  headData={[
                    "product_name",
                    "GST%",
                    "Empty Return",
                    "Unit Rate",
                    "discount",
                    "Quantity",
                    "Taxable Amount",
                    "GST Amount",
                    "vehicle no",
                  ]}
                  body={
                    <>
                      {products_array?.map((v, i) => (
                        <Tr key={i}>
                          <Td>{v?.p_detail?.description}</Td>
                          <Td>{v.gst}</Td>
                          <Td>{v.emptyReturn}</Td>
                          <Td>{v.unit_rate}</Td>
                          <Td>{formatIndianPrice(v.discount)}</Td>
                          <Td>{v.quantity}</Td>
                          <Td>{formatIndianPrice(v.taxable_amount)}</Td>
                          <Td>{formatIndianPrice(v.gst_amount)}</Td>
                          <Td>{v.vehicle_no}</Td>
                        </Tr>
                      ))}

                      <Tr>
                        <Td>
                          <CustomSingleInput
                            width={"200px"}
                            placeholder={"Product"}
                            select
                            disabled={!values.omc}
                            options={filtered_product_list(values.omc)}
                            value={single_product?.product || ""}
                            onChange={(e) => {
                              const selectedValue = e.target.value;

                              const p_detail = filtered_product_list(
                                values.omc
                              )?.find((item) => item.uuid === selectedValue);

                              setSingle_product((prev) => ({
                                ...prev,
                                product: selectedValue,
                                p_detail: p_detail,
                              }));
                              fetchGST(values.omc, "gst", selectedValue);
                            }}
                            error={singleProductErrors.product}
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"100px"}
                            placeholder={"GST"}
                            disabled
                            value={single_product?.gst || ""}
                          />
                        </Td>

                        <Td>
                          <CustomSingleInput
                            width={"200px"}
                            placeholder={"Empty Return"}
                            value={single_product?.emptyReturn || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*$/.test(value)) {
                                handleChangeProduct("emptyReturn", value);
                              }
                            }}
                            error={singleProductErrors.emptyReturn || ""}
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"120px"}
                            placeholder={"Unit Rate"}
                            value={single_product?.unit_rate || ""}
                            onChange={(v) =>
                              handleChangeProduct(
                                "unit_rate",
                                parseFloat(v.target.value) || 0
                              )
                            }
                            error={singleProductErrors.unit_rate}
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"120px"}
                            placeholder={"Discount"}
                            value={single_product?.discount || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*$/.test(value)) {
                                handleChangeProduct("discount", value);
                              }
                            }}
                            error={singleProductErrors.discount}
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"100px"}
                            placeholder={"Quantity"}
                            value={single_product?.quantity || ""}
                            onChange={(v) =>
                              handleChangeProduct(
                                "quantity",
                                parseFloat(v.target.value) || 0
                              )
                            }
                            error={singleProductErrors.quantity}
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"170px"}
                            placeholder={"Taxable Amount"}
                            value={single_product?.taxable_amount}
                            disabled
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"150px"}
                            placeholder={"GST Amount"}
                            value={single_product?.gst_amount}
                            disabled
                          />
                        </Td>
                        <Td>
                          <CustomSingleInput
                            width={"150px"}
                            placeholder={"Vehicle Number"}
                            value={single_product?.vehicle_no || ""}
                            onChange={(v) =>
                              handleChangeProduct("vehicle_no", v.target.value)
                            }
                            error={singleProductErrors.vehicle_no}
                          />
                        </Td>
                      </Tr>
                    </>
                  }
                />

                <Flex justifyContent={"flex-end"} marginBlock={5} gap={2}>
                  <CustomButton
                    title={"Add"}
                    onClick={() => {
                      setTimeout(() => submitForm(), 0);
                    }}
                    // disabled={isAddDisabled()}
                  />
                  <CustomButton
                    title={"Calculate"}
                    onClick={calculate_amount}
                    disabled={
                      !single_product?.unit_rate ||
                      !single_product?.quantity ||
                      isValidatingSingleProduct
                    }
                  />
                  <CustomButton
                    title={"Save"}
                    onClick={() => {
                      if (products_array.length === 0) {
                        toast.error("Please add at least one product");
                        return;
                      }
                      handleSubmit(values);
                    }}
                    disabled={products_array.length === 0 || isSubmitting}
                  />
                </Flex>
              </Card>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddPurchase;
